import React, { useEffect } from 'react';
import { Form, Input, Button, Select, Card, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addBDE, updateBDE } from '../store/bdeSlice';
import { formatName, formatMobile } from '../utils/formatters';

const { Option } = Select;

const BDEForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { users } = useSelector((state) => state.bde);
  const { orders } = useSelector((state) => state.orders);

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      const user = users.find((u) => u.id === id);
      if (user) {
        // Transform workingPincodes array to comma-separated string for display
        const formData = {
          ...user,
          workingPincodes: Array.isArray(user.workingPincodes)
            ? user.workingPincodes.join(', ')
            : user.workingPincodes
        };
        form.setFieldsValue(formData);
      } else {
        message.error('User not found');
        navigate('/admin/bde-users');
      }
    }
  }, [id, isEditMode, users, form, navigate]);

  const onFinish = (values) => {
    const formattedValues = {
      ...values,
      name: formatName(values.name),
      mobile: formatMobile(values.mobile),
      workingPincodes: typeof values.workingPincodes === 'string'
        ? values.workingPincodes.split(',').map(p => p.trim()).filter(Boolean)
        : values.workingPincodes,
    };

    if (isEditMode) {
      const originalUser = users.find((u) => u.id === id);

      // Check for deactivation attempt with unpaid commissions
      if (originalUser && originalUser.status === 'verified' && values.status !== 'verified') {
        const hasUnpaid = orders.some(o => o.bdeId === id && !o.commissionPaid && ['Expected', 'Processing', 'Completed'].includes(o.status));
        if (hasUnpaid) {
          message.error('Cannot deactivate user with unpaid commissions');
          return;
        }
      }

      dispatch(updateBDE({ ...formattedValues, id }))
        .unwrap()
        .then(() => {
          message.success('BDE User updated');
          navigate('/admin/bde-users');
        })
        .catch((err) => message.error('Failed to update BDE: ' + err));
    } else {
      const newUser = {
        ...formattedValues,
        id: `BDE-${Date.now()}`, // Simple ID generation
        createdAt: new Date().toISOString().split('T')[0],
        status: values.status || 'pending', // Default to pending if not set
        role: 'bde',
      };
      dispatch(addBDE(newUser))
        .unwrap()
        .then(() => {
          message.success('BDE User created');
          navigate('/admin/bde-users');
        })
        .catch((err) => message.error('Failed to create BDE: ' + err));
    }
  };

  return (
    <Card title={isEditMode ? 'Edit BDE User' : 'Create BDE User'}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ status: 'pending' }}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please input the email!' },
            { type: 'email', message: 'Please enter a valid email!' }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please input the name!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="mobile"
          label="Mobile Number"
          rules={[{ required: true, message: 'Please input the mobile number!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="whatsappNumber"
          label="WhatsApp Number"
          rules={[
            { required: true, message: 'Please input WhatsApp number!' },
            { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit number!' }
          ]}
        >
          <Input maxLength={10} />
        </Form.Item>

        <Form.Item
          name="pincode"
          label="Pincode"
          rules={[
            { required: true, message: 'Please input the pincode!' },
            { pattern: /^[0-9]{6}$/, message: 'Please enter a valid 6-digit pincode!' }
          ]}
        >
          <Input maxLength={6} />
        </Form.Item>

        <Form.Item
          name="address"
          label="Address"
          rules={[{ required: true, message: 'Please input the address!' }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="companyName"
          label="Working Company Name"
          rules={[{ required: true, message: 'Please input company name!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="workingPincodes"
          label="Working Pincodes (comma separated)"
          rules={[{ required: true, message: 'Please input working pincodes!' }]}
        >
          <Input placeholder="110001, 110002" />
        </Form.Item>

        <Form.Item
          name="accountNumber"
          label="AC No."
          rules={[{ required: true, message: 'Please input account number!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="ifsc"
          label="IFSC Code"
          rules={[
            { required: true, message: 'Please enter IFSC code' },
            { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'Invalid IFSC Code format' }
          ]}
        >
          <Input placeholder="e.g. HDFC0001234" style={{ textTransform: 'uppercase' }} />
        </Form.Item>

        <Form.Item
          name="branchName"
          label="Bank name"
          rules={[{ required: true, message: 'Please enter bank name' }]}
        >
          <Input placeholder="e.g. HDFC Bank" />
        </Form.Item>

        <Form.Item
          name="upiId"
          label="UPI ID"
          rules={[{ required: true, message: 'Please enter UPI ID' }]}
        >
          <Input placeholder="e.g. john@upi" />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select the status!' }]}
        >
          <Select>
            <Option value="pending">Pending</Option>
            <Option value="verified">Verified</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {isEditMode ? 'Update' : 'Create'}
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate('/admin/bde-users')}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default BDEForm;
